import { useMutation } from '@apollo/client';
import { type FC, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Navigate,
    useNavigate,
    useParams,
    useSearchParams,
} from 'react-router-dom';

import {
    Body1,
    Button,
    Heading2,
    Stack,
} from '@myphx-engineering/phx-design-system';

import {
    useFillPathway,
    useFillPrescription,
} from '../../../contexts/fill/use-fill-prescription';
import { SendOfferDocument } from '../../../graphql/generated/graphql';
import { usePatientContext } from '../../../hooks/use-patient-context';
import { useProvidersFromOffers } from '../../../hooks/use-providers-from-offers';
import { useRefetchQueries } from '../../../hooks/use-refetch-queries';
import { getAbsoluteRoute } from '../../../util/get-absolute-route';
import { getRefetchQueryForPrescription } from '../../../util/get-refetch-query-for-prescription';
import { ProviderInfo } from '../../provider/ProviderInfo';

import { PharmacyPreviewSubCta } from './common/PharmacyPreviewSubCta';
import { PricingCard } from './common/PricingCard';
import { usePatientChoiceMutation } from './common/use-patient-choice-mutation';

export const PharmacyPreviewScreen: FC = () => {
    const { t } = useTranslation('lilly');
    const { offerId, pharmacyId } = useParams();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { patient: userPatient } = usePatientContext();

    const { coverageCard, drxId, offers } = useFillPrescription();
    const { originalPathway, pathway } = useFillPathway();

    const providers = useProvidersFromOffers(offers);

    const selectedProvider = providers.find(
        (provider) => provider.id === pharmacyId
    );
    const selectedOffer = offers.find((offer) => offer.id === offerId);

    const savePatientChoices = usePatientChoiceMutation({
        drxId,
    });

    const [sendOffer, { loading }] = useMutation(SendOfferDocument);

    const {
        refetch,
        isComplete: isRefetchComplete,
        isLoading: isRefetchLoading,
    } = useRefetchQueries(
        getRefetchQueryForPrescription(drxId, userPatient.id)
    );

    // TODO: replace with subPathway from useFillPathway once available (Bill's next PR)
    const subPathway = searchParams.get('sub-pathway');

    const handleSend = useCallback(async () => {
        if (!offerId) {
            return;
        }

        savePatientChoices({ pathway, originalPathway, subPathway });

        const result = await sendOffer({
            variables: {
                offerId,
                coverageCard: coverageCard
                    ? {
                          bin: coverageCard.bin,
                          cardHolderId: coverageCard.cardHolderId,
                          coverageCardType: coverageCard.coverageCardType,
                          groupId: coverageCard.groupId,
                          pcn: coverageCard.pcn,
                      }
                    : undefined,
            },
        });

        if (result.data?.sendOffer?.success) {
            void refetch();
        } else {
            throw new Error('Failed to send offer');
        }
    }, [
        coverageCard,
        offerId,
        originalPathway,
        pathway,
        refetch,
        savePatientChoices,
        sendOffer,
        subPathway,
    ]);

    useEffect(() => {
        if (isRefetchComplete) {
            navigate(
                getAbsoluteRoute('order.prescriberOrderId.confirmation', {
                    prescriberOrderId: drxId,
                })
            );
        }
    }, [drxId, isRefetchComplete, navigate]);

    if (!selectedOffer || !selectedProvider) {
        return (
            <Navigate
                replace
                to={{
                    pathname: getAbsoluteRoute('fill.drxId.pharmacy.root', {
                        drxId,
                    }),
                    search: `?${searchParams.toString()}`,
                }}
            />
        );
    }

    return (
        <Stack gap="lg" data-testid="pharmacy-preview-page-root">
            <PricingCard offer={selectedOffer} />

            <Stack gap="sm">
                <Heading2>{t('previewDetails.pharmacy.header')}</Heading2>
                <Body1>{t('providerDetails.pharmacy.description')}</Body1>
                <ProviderInfo provider={selectedProvider} />
            </Stack>

            <Button onClick={handleSend} loading={isRefetchLoading || loading}>
                {t('previewDetails.sendToPharmacy')}
            </Button>

            <PharmacyPreviewSubCta />
        </Stack>
    );
};
